import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, UNITS } from "@/lib/api";
import { brl, fmtDate, numStr, parseNum } from "@/lib/format";
import { Button, Card, Chip, Field, Input, Loading, Page, PageHeader, Switch, StickyFooter } from "@/components/kit";
import { FormActions } from "@/components/FormActions";
import { SelectCreate } from "@/components/SelectCreate";

const copyOf = (f) => ({ name: `${f.name} (cópia)`, category_id: f.category_id, sector_id: f.sector_id, unit: f.unit, unit_cost: f.unit_cost, active: f.active, sales_code: "" });

export default function ProductForm() {
  const { id } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: prod, isLoading } = useQuery({ queryKey: ["product", id], queryFn: () => api(`/products/${id}`), enabled: !!id });
  const { data: hist = [] } = useQuery({ queryKey: ["cost-history", id], queryFn: () => api(`/products/${id}/cost-history`), enabled: !!id });
  const EMPTY_P = { name: "", category_id: "", sector_id: null, unit: "un", unit_cost: "", active: true, sales_code: "" };
  const [f, setF] = useState(() => (id ? null : loc.state?.copy) || EMPTY_P);
  const [base, setBase] = useState(JSON.stringify(EMPTY_P));
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { if (prod) { const s = { ...prod, unit_cost: numStr(prod.unit_cost), sales_code: prod.sales_code || "" }; setF(s); setBase(JSON.stringify(s)); } }, [prod]);

  const refresh = () => ["products", "categories", "sectors", "conference"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const done = (msg) => { refresh(); toast.success(msg); nav("/products"); };
  const save = useMutation({
    mutationFn: () => api(id ? `/products/${id}` : "/products", { method: id ? "PUT" : "POST", body: { name: f.name, category_id: f.category_id, sector_id: f.sector_id, unit: f.unit, unit_cost: parseNum(f.unit_cost), active: f.active, sales_code: f.sales_code } }),
    onSuccess: (_d, andNew) => {
      if (!andNew) return done(id ? "Produto atualizado" : "Produto cadastrado");
      refresh();
      toast.success("Produto cadastrado · pronto para o próximo");
      const next = { ...EMPTY_P, category_id: f.category_id, sector_id: f.sector_id, unit: f.unit };
      setF(next); setBase(JSON.stringify(next));
      if (loc.state?.copy) nav("/products/new", { replace: true, state: null });
      document.querySelector('[data-testid="product-name-input"]')?.focus();
    },
    onError: (e) => setErr(e.message),
  });
  const del = useMutation({ mutationFn: () => api(`/products/${id}`, { method: "DELETE" }), onSuccess: () => done("Produto excluído"), onError: (e) => toast.error(e.message) });
  const submit = (andNew = false) => {
    setErr("");
    if (!f.name.trim()) return setErr("Informe o nome do produto");
    if (!f.category_id) return setErr("Selecione uma categoria");
    save.mutate(andNew);
  };
  if (id && isLoading) return <Loading />;
  return (
    <Page narrow>
      <PageHeader title={id ? "Editar produto" : loc.state?.copy ? "Novo produto (cópia)" : "Novo produto"} close
        right={id ? <Button variant="secondary" size="sm" icon={Copy} onClick={() => nav("/products/new", { state: { copy: copyOf(f) } })} data-testid="duplicate-product-button">Duplicar</Button> : null} />
      <Card className="flex flex-col gap-5">
        <Field label="Nome"><Input placeholder="Ex.: Filé mignon" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="product-name-input" /></Field>
        <SelectCreate label="Categoria" endpoint="/categories" value={f.category_id} onChange={(v) => setF((x) => ({ ...x, category_id: v }))} testid="product-category-select" />
        <SelectCreate label="Setor de contagem" endpoint="/sectors" value={f.sector_id} onChange={(v) => setF((x) => ({ ...x, sector_id: v || null }))} allowNone noneLabel="Sem setor" newLabel="Novo setor" createdMsg="Setor criado" testid="product-sector-select" />
        <div className="flex flex-col gap-2">
          <span className="label">Unidade de medida</span>
          <div className="flex flex-wrap gap-2">{UNITS.map((u) => <Chip key={u} active={f.unit === u} onClick={() => setF({ ...f, unit: u })} data-testid={`product-unit-${u}`}>{u}</Chip>)}</div>
        </div>
        <Field label={`Custo por ${f.unit} (R$)`}><Input inputMode="decimal" placeholder="0,00" value={f.unit_cost} onChange={(e) => setF({ ...f, unit_cost: e.target.value })} data-testid="product-cost-input" /></Field>
        <Field label="Código no caixa / PDV" hint="Mesmo código da planilha de vendas · para rastrear itens vendidos direto (bebidas, sobremesas prontas) no CMV teórico x real">
          <Input placeholder="Ex.: 2045" value={f.sales_code} onChange={(e) => setF({ ...f, sales_code: e.target.value })} data-testid="product-sales-code-input" />
        </Field>
        {hist.length > 0 && (
          <div className="rounded-xl p-3" style={{ background: "var(--surface-alt)" }} data-testid="product-cost-history">
            <div className="label mb-2">Custo atualizado pelas notas</div>
            {hist.map((h) => <div key={h.id} className="flex justify-between caption py-0.5"><span>{fmtDate(h.created_at)} · {h.supplier}{h.invoice_number ? ` · NF ${h.invoice_number}` : ""}</span><span className="tnum">{brl(h.unit_cost)}</span></div>)}
          </div>
        )}
        <div className="flex items-center gap-4">
          <div className="flex-1"><div className="label">Produto ativo</div><div className="caption">Somente produtos ativos entram nas novas conferências.</div></div>
          <Switch on={f.active} onChange={(v) => setF({ ...f, active: v })} data-testid="product-active-switch" />
        </div>
        {err && <span className="caption" style={{ color: "var(--danger)" }} data-testid="product-form-error">{err}</span>}
        {id && (confirm ? (
          <div className="flex gap-2"><Button variant="secondary" size="sm" onClick={() => setConfirm(false)} data-testid="cancel-delete-product">Cancelar</Button><Button variant="danger" size="sm" loading={del.isPending} onClick={() => del.mutate()} data-testid="confirm-delete-product">Confirmar exclusão</Button></div>
        ) : <Button variant="ghost" size="sm" icon={Trash2} className="self-start" onClick={() => setConfirm(true)} data-testid="delete-product-button">Excluir produto</Button>)}
      </Card>
      <StickyFooter>
        <FormActions testid="product" saveLabel="Salvar produto" saving={save.isPending ? (save.variables ? "new" : "save") : null} dirty={JSON.stringify(f) !== base}
          onSave={() => submit(false)} onSaveNew={id ? null : () => submit(true)} onBack={() => nav("/products")} />
      </StickyFooter>
    </Page>
  );
}
